<template>
  <div class="admin-sites">
    <div class="page-header">
      <h2>Lugares y rutas</h2>
      <v-btn
        v-if="pestana === 'lugares'"
        color="primary"
        prepend-icon="mdi-plus"
        @click="abrirDialogo()"
      >
        Nuevo lugar
      </v-btn>
    </div>

    <!-- Las rutas viven aqui y no en pantalla aparte: son contenido curado del
         mismo tipo, y agrupan justo los lugares que se administran al lado. -->
    <v-tabs v-model="pestana" color="primary" class="pestanas">
      <v-tab value="lugares">
        <v-icon start size="18">mdi-map-marker-multiple</v-icon>
        Lugares
      </v-tab>
      <v-tab value="rutas">
        <v-icon start size="18">mdi-map-marker-path</v-icon>
        Rutas
      </v-tab>
    </v-tabs>

    <PanelRutas v-if="pestana === 'rutas'" class="mt-4" />

    <template v-else>
    <!-- Filtros -->
    <div class="filtros">
      <v-text-field
        v-model="filtroTexto"
        label="Buscar por nombre o ciudad"
        prepend-inner-icon="mdi-magnify"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        @keydown.enter="buscar"
        @click:clear="limpiarFiltros"
      />
      <v-select
        v-model="filtroCategoria"
        :items="categorias"
        item-title="name"
        item-value="idCategory"
        label="Categoría"
        variant="outlined"
        density="compact"
        hide-details
        clearable
      />
      <v-btn color="primary" variant="tonal" @click="buscar">Buscar</v-btn>
    </div>

    <!-- Lista de lugares -->
    <div v-if="cargando" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="lugares.length === 0" class="empty-state">
      <v-icon size="52" color="grey-lighten-1">mdi-map-marker-off</v-icon>
      <p class="text-grey mt-2">No se encontró ningún lugar.</p>
      <p class="text-body-2 mt-1 mb-3">¿Conoces un lugar que deba aparecer aquí? Escríbenos.</p>
      <v-btn
        color="green"
        variant="tonal"
        prepend-icon="mdi-whatsapp"
        :href="`https://wa.me/57${whatsappNumber}`"
        target="_blank"
        rel="noopener"
      >
        Contáctanos al {{ whatsappNumber }}
      </v-btn>
    </div>

    <div v-else class="lista-lugares">
      <v-card
        v-for="lugar in lugares"
        :key="lugar.idSite"
        class="lugar-card"
        variant="outlined"
      >
        <v-card-text class="lugar-info">
          <div class="lugar-nombre">{{ lugar.name }}</div>
          <div class="lugar-ubicacion">
            <v-icon size="14">mdi-map-marker</v-icon>
            {{ [lugar.city, lugar.department, lugar.country].filter(Boolean).join(", ") || "Sin ubicación" }}
          </div>
          <div class="lugar-chips">
            <v-chip
              v-for="cat in lugar.categories"
              :key="cat.idCategory"
              size="x-small"
              color="green"
              variant="tonal"
            >
              {{ cat.name }}
            </v-chip>
            <v-chip v-if="lugar.freeEntry" size="x-small" color="blue" variant="tonal">
              Entrada libre
            </v-chip>
          </div>
        </v-card-text>
        <v-card-actions class="lugar-acciones">
          <v-btn size="small" variant="tonal" color="primary" @click="abrirDialogo(lugar)">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn size="small" variant="tonal" color="error" @click="confirmarEliminar(lugar)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>

    <!-- Paginación -->
    <div v-if="totalPaginas > 1" class="paginacion">
      <v-btn :disabled="paginaActual === 0" variant="text" @click="cambiarPagina(paginaActual - 1)">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <span>{{ paginaActual + 1 }} / {{ totalPaginas }}</span>
      <v-btn :disabled="esUltima" variant="text" @click="cambiarPagina(paginaActual + 1)">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    </template>

    <!-- Diálogo crear/editar -->
    <v-dialog v-model="dialogo" max-width="600" @after-leave="limpiarDialogo">
      <v-card>
        <v-card-title>{{ modoEdicion ? "Editar lugar" : "Nuevo lugar" }}</v-card-title>
        <v-card-text>
          <v-alert v-if="errorForm" type="error" variant="tonal" class="mb-3">
            {{ errorForm }}
          </v-alert>
          <v-text-field v-model="form.name" label="Nombre *" variant="outlined" density="compact" class="mb-2" maxlength="255" />
          <CategoryField v-model="form.categories" entity-type="lugar" class="mb-2" />
          <div class="grid-2">
            <v-text-field v-model="form.country" label="País" variant="outlined" density="compact" maxlength="255" />
            <v-text-field v-model="form.department" label="Departamento" variant="outlined" density="compact" maxlength="255" />
            <v-text-field v-model="form.city" label="Ciudad" variant="outlined" density="compact" maxlength="255" />
            <v-text-field v-model="form.schedule" label="Horario" variant="outlined" density="compact" maxlength="300" />
          </div>
          <v-textarea
            v-model="form.description"
            label="Descripción"
            variant="outlined"
            density="compact"
            rows="4"
            class="mb-2"
            :counter="2000"
            maxlength="2000"
          />
          <!-- El contador y el maxlength existen porque aqui se pegan URLs
               de Google Maps y la columna tiene un tope: mejor avisar mientras
               se escribe que dejar que el servidor responda un error. -->
          <v-text-field
            v-model="form.location"
            label="Ubicación (dirección o referencia)"
            variant="outlined"
            density="compact"
            class="mb-2"
            :counter="1000"
            maxlength="1000"
            persistent-counter
            hint="Puedes pegar el enlace de Google Maps"
          />
          <div class="grid-2">
            <v-text-field v-model="form.latitude" label="Latitud" variant="outlined" density="compact" />
            <v-text-field v-model="form.longitude" label="Longitud" variant="outlined" density="compact" />
          </div>
          <v-checkbox v-model="form.freeEntry" label="Entrada libre" density="compact" hide-details />

          <!-- Imágenes (solo en modo edición) -->
          <template v-if="modoEdicion">
            <v-divider class="my-3" />
            <div class="imagenes-header">
              <span class="text-subtitle-2">Imágenes</span>
              <v-btn size="small" color="primary" variant="tonal" prepend-icon="mdi-plus" @click="dialogoImagen = true">
                Agregar
              </v-btn>
            </div>
            <div v-if="imagenes.length === 0" class="text-grey text-caption mt-1">Sin imágenes</div>
            <div class="grid-imagenes mt-2">
              <div v-for="img in imagenes" :key="img.idImagen" class="img-wrapper">
                <v-img :src="img.pathBase64" cover height="110" class="rounded" />
                <v-btn
                  icon
                  size="x-small"
                  color="error"
                  class="btn-del-img"
                  @click="eliminarImagen(img.idImagen)"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogo = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="guardando" @click="guardar">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo subir imagen -->
    <v-dialog v-model="dialogoImagen" max-width="620" :persistent="subiendoImagenes">
      <EntryLoalImage
        ref="entradaImagenes"
        multiple
        :subiendo="subiendoImagenes"
        :progreso="progresoSubida"
        @onImageSelected="subirImagen"
      />
    </v-dialog>

    <!-- Diálogo confirmar eliminar -->
    <v-dialog
      v-model="dialogoEliminar"
      max-width="420"
      :persistent="eliminando"
    >
      <v-card class="modal modal-confirmar">
        <div class="modal-confirmar-cabecera">
          <div class="modal-confirmar-icono">
            <v-icon size="20">
              mdi-alert-outline
            </v-icon>
          </div>
          <h2 class="modal-confirmar-titulo">
          Eliminar lugar
          </h2>
        </div>

        <v-card-text class="modal-confirmar-cuerpo">
          <p>Se va a eliminar <strong>{{ lugarAEliminar?.name }}</strong>.</p>
          <p class="modal-irreversible">Esta acción no se puede deshacer.</p>
        </v-card-text>
        <v-card-actions class="modal-confirmar-pie">
          <v-spacer />
          <v-btn variant="text" :disabled="eliminando" @click="dialogoEliminar = false">Cancelar</v-btn>
          <v-btn color="red" variant="flat" :loading="eliminando" @click="eliminar">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :timeout="3500" :color="colorSnackbar">
      {{ mensajeSnackbar }}
    </v-snackbar>
  </div>
</template>

<script>
import apisSites from "@/apis/apisSites";
import { mensajeDeError } from "@/utils/errores";
import EntryLoalImage from "@/components/Fields/EntryLoalImage.vue";
import CategoryField from "@/components/Fields/CategoryField.vue";
import PanelRutas from "@/components/Rutas/PanelRutas.vue";
import Config from "@/Config";

export default {
  name: "AdminSitesView",
  components: { EntryLoalImage, CategoryField, PanelRutas },
  data() {
    return {
      pestana: "lugares",
      lugares: [],
      categorias: [],
      cargando: false,
      filtroTexto: "",
      filtroCategoria: null,
      paginaActual: 0,
      totalPaginas: 1,
      esUltima: true,

      whatsappNumber: Config.whatsapp,

      dialogo: false,
      modoEdicion: false,
      guardando: false,
      errorForm: null,
      form: this.formVacio(),

      imagenes: [],
      dialogoImagen: false,
      subiendoImagenes: false,
      progresoSubida: "",

      snackbar: false,
      mensajeSnackbar: "",
      colorSnackbar: "success",

      dialogoEliminar: false,
      eliminando: false,
      lugarAEliminar: null,
    };
  },
  created() {
    this.cargarCategorias();
    this.buscar();
  },
  methods: {
    formVacio() {
      return {
        idSite: null,
        name: "",
        country: "",
        department: "",
        city: "",
        description: "",
        location: "",
        latitude: null,
        longitude: null,
        schedule: "",
        freeEntry: true,
        views: 0,
        categories: [],
      };
    },
    async cargarCategorias() {
      this.categorias = await apisSites.getCategoriesByEntityType("lugar");
    },
    async buscar() {
      this.paginaActual = 0;
      await this.cargarLugares();
    },
    limpiarFiltros() {
      this.filtroTexto = "";
      this.filtroCategoria = null;
      this.buscar();
    },
    async cargarLugares() {
      this.cargando = true;
      try {
        const resp = await apisSites.search({
          page: this.paginaActual,
          text: this.filtroTexto || null,
          idCategory: this.filtroCategoria || null,
        });
        this.lugares = resp.content;
        this.totalPaginas = resp.totalPages;
        this.esUltima = resp.last;
      } finally {
        this.cargando = false;
      }
    },
    async cambiarPagina(nuevaPagina) {
      this.paginaActual = nuevaPagina;
      await this.cargarLugares();
    },
    abrirDialogo(lugar = null) {
      if (lugar) {
        this.modoEdicion = true;
        this.form = {
          idSite: lugar.idSite,
          name: lugar.name,
          country: lugar.country || "",
          department: lugar.department || "",
          city: lugar.city || "",
          description: lugar.description || "",
          location: lugar.location || "",
          latitude: lugar.latitude,
          longitude: lugar.longitude,
          schedule: lugar.schedule || "",
          freeEntry: lugar.freeEntry ?? true,
          views: lugar.views,
          categories: lugar.categories || [],
        };
      } else {
        this.modoEdicion = false;
        this.form = this.formVacio();
      }
      this.errorForm = null;
      this.dialogo = true;
      if (lugar) this.cargarImagenes();
    },
    limpiarDialogo() {
      this.form = this.formVacio();
      this.errorForm = null;
      this.modoEdicion = false;
      this.imagenes = [];
    },
    async cargarImagenes() {
      if (!this.form.idSite) return;
      this.imagenes = await apisSites.getImages(this.form.idSite);
    },
    /**
     * Recibe una o varias imagenes en base64. Se suben de a una y no en
     * paralelo: cada una viaja como base64 dentro del JSON y mandarlas todas
     * juntas satura el servidor.
     */
    async subirImagen(imagenes) {
      const lista = Array.isArray(imagenes) ? imagenes : [imagenes];

      if (lista.length === 0) {
        return;
      }

      this.subiendoImagenes = true;
      let fallidas = 0;

      for (let i = 0; i < lista.length; i++) {
        this.progresoSubida = `Subiendo ${i + 1} de ${lista.length}...`;

        try {
          await apisSites.saveImage(this.form.idSite, lista[i]);
        } catch (error) {
          console.error("Error subiendo imagen:", error);
          fallidas++;
        }
      }

      this.subiendoImagenes = false;
      this.progresoSubida = "";
      await this.cargarImagenes();

      const subidas = lista.length - fallidas;

      if (fallidas === 0) {
        this.avisar(`${subidas} imagen(es) subida(s)`, "success");
        if (this.$refs.entradaImagenes) {
          this.$refs.entradaImagenes.limpiar();
        }
        this.dialogoImagen = false;
      } else {
        // El dialogo se queda abierto para poder reintentar con las que fallaron
        this.avisar(
          `${subidas} subida(s), ${fallidas} fallaron. Puede que sean muy grandes o de un formato no soportado.`,
          "error"
        );
      }
    },
    avisar(mensaje, color) {
      this.mensajeSnackbar = mensaje;
      this.colorSnackbar = color;
      this.snackbar = true;
    },
    async eliminarImagen(idImagen) {
      await apisSites.deleteImage(idImagen);
      await this.cargarImagenes();
    },
    async guardar() {
      this.guardando = true;
      this.errorForm = null;
      try {
        const payload = {
          ...this.form,
          categories: this.form.categories.map((c) =>
            typeof c === "object" ? { idCategory: c.idCategory } : { idCategory: c }
          ),
        };
        const resp = await apisSites.save(payload);
        if (typeof resp === "string") {
          this.errorForm = resp;
        } else {
          this.dialogo = false;
          await this.cargarLugares();
        }
      } catch (e) {
        this.errorForm = mensajeDeError(e, "No se pudo guardar el lugar");
      } finally {
        this.guardando = false;
      }
    },
    confirmarEliminar(lugar) {
      this.lugarAEliminar = lugar;
      this.dialogoEliminar = true;
    },
    async eliminar() {
      this.eliminando = true;
      try {
        await apisSites.delete(this.lugarAEliminar.idSite);
        this.dialogoEliminar = false;
        await this.cargarLugares();
      } catch {
        // silencioso
      } finally {
        this.eliminando = false;
        this.lugarAEliminar = null;
      }
    },
  },
};
</script>

<style scoped>
.admin-sites {
  max-width: 700px;
  margin: 0 auto;
  padding: 16px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.page-header h2 {
  font-size: 1.3rem;
  font-weight: 600;
}

.filtros {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: center;
}

.filtros .v-text-field,
.filtros .v-select {
  flex: 1;
  min-width: 140px;
}

.lista-lugares {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.lugar-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.lugar-info {
  flex: 1;
  padding-bottom: 8px !important;
}

.lugar-nombre {
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 4px;
}

.lugar-ubicacion {
  font-size: 0.82rem;
  color: #666;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 2px;
}

.lugar-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.lugar-acciones {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px !important;
  align-self: center;
}

.paginacion {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 8px;
}

.imagenes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.grid-imagenes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.img-wrapper {
  position: relative;
  border: 2px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
}

.btn-del-img {
  position: absolute;
  top: 4px;
  right: 4px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 16px;
  text-align: center;
}
</style>
